import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { userScopeCleared } from '@krp/client-runtime'

/**
 * Намерение человека в окне расхождений: какие строки отмечены и в какие источники их применить. Только выбор — сами строки живут
 * в кэше запросов. Выбор не сохраняется: после обновления страницы окно открывается без отметок.
 */
export interface SelectionState {
  /** Источник открытого окна расхождений (ресурс или `catalog`); другой источник начинает выбор заново. */
  source: string | null
  ids: number[]
  targets: string[]
}

const initialState: SelectionState = { source: null, ids: [], targets: [] }

const toggled = <T>(list: readonly T[], id: T): T[] => (list.includes(id) ? list.filter((item) => item !== id) : [...list, id])

const selectionSlice = createSlice({
  name: 'sync/selection',
  initialState,
  reducers: {
    sourceOpened(state, action: PayloadAction<string>) {
      if (state.source === action.payload) return
      return { source: action.payload, ids: [], targets: [] }
    },
    rowToggled(state, action: PayloadAction<number>) {
      state.ids = toggled(state.ids, action.payload)
    },
    /** «Выбрать все» на странице: отмечает все строки страницы или снимает отметку со всех; прочие отметки не трогает. */
    rowsSet(state, action: PayloadAction<{ ids: readonly number[]; selected: boolean }>) {
      const { ids, selected } = action.payload
      state.ids = selected ? [...new Set([...state.ids, ...ids])] : state.ids.filter((id) => !ids.includes(id))
    },
    targetToggled(state, action: PayloadAction<string>) {
      state.targets = toggled(state.targets, action.payload)
    },
    /** Отметки сделали своё дело (применили, перестали отслеживать) или сменился показанный список. Источники-цели остаются. */
    rowsCleared(state) {
      state.ids = []
    },
  },
  extraReducers: (builder) => {
    // Выход или смена человека на устройстве стирает и выбор: следующий человек его не увидит.
    builder.addCase(userScopeCleared, () => initialState)
  },
})

export const { sourceOpened, rowToggled, rowsSet, targetToggled, rowsCleared } = selectionSlice.actions
export const selectionReducer = selectionSlice.reducer
